import React, { useState } from "react";

import { faInfoCircle } from "@performyard/fontawesome/pro-regular-svg-icons";
import { Icon, Spinner, Text, TextEditor } from "@performyard/py-components";
import { countTranscriptCharacters } from "@performyard/service-contracts";

import AccordionSectionHeader from "./AccordionSectionHeader";
import SaveStatus from "./SaveStatus";
import TranscriptIngestBanner from "./TranscriptIngestBanner";
import TranscriptSourceNotation from "./TranscriptSourceNotation";
import TranscriptStatusChip from "./TranscriptStatusChip";

import { useMeetingTranscript, useRbacObjectPermissions, useUpdateMeetingTranscript } from "../../../../api/hooks";
import useAutosave from "../../../../hooks/useAutosave";
import { useTranscriptState } from "../hooks";

import { config } from "../config";
import { useMeetingContext } from "../context";

/**
 * Meeting transcript accordion section for the right side panel.
 * Collapsible section with character limit and autosave.
 * Edit/view access is governed by RBAC (meetings:editTranscript / meetings:viewTranscript).
 */
const MeetingTranscriptSection = (): React.ReactElement => {
	const { meeting } = useMeetingContext();
	const [isExpanded, setIsExpanded] = useState(false);

	const { data: transcriptData, isLoading: isTranscriptLoading } = useMeetingTranscript(meeting._id);
	const { mutate: updateMeetingTranscript, isError: isTranscriptError } = useUpdateMeetingTranscript();

	const { data: rbacPermissions } = useRbacObjectPermissions("meeting", meeting._id);
	const canEditTranscript = rbacPermissions?.includes("meetings:editTranscript") ?? false;

	const { transcriptContent, setTranscriptContent } = useTranscriptState({
		transcriptData,
		isTranscriptLoading,
	});

	const {
		showSaved: showTranscriptSaved,
		showSaving: showTranscriptSaving,
		handleChange: handleTranscriptChange,
	} = useAutosave({
		value: transcriptContent,
		setValue: setTranscriptContent,
		onSave: async (content) => {
			if (countTranscriptCharacters(content) <= config.maxTranscriptLength) {
				updateMeetingTranscript({
					meetingId: meeting._id,
					content: content,
				});
			}
		},
		delay: config.autosaveDelay,
		enabled: canEditTranscript,
		minSavingTime: config.minSavingTime,
	});

	const isWithinLimit = countTranscriptCharacters(transcriptContent) <= config.maxTranscriptLength;

	return (
		<div className="flex flex-col border-b border-solid border-neutral-2 bg-white">
			<AccordionSectionHeader
				title="Meeting Transcript"
				isExpanded={isExpanded}
				onToggle={() => setIsExpanded(!isExpanded)}
				headerAccessory={<TranscriptStatusChip />}
			/>

			{isExpanded && (
				<div className="flex flex-col gap-2 p-4">
					{isTranscriptLoading ? (
						<div className="flex items-center justify-center py-4">
							<Spinner size={config.spinnerSize.small} />
						</div>
					) : (
						<>
							<TranscriptIngestBanner />
							<div className="flex h-5 items-center justify-between">
								<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
									Paste in Transcript
								</Text>
								<SaveStatus
									isSaving={showTranscriptSaving && isWithinLimit}
									isSaved={showTranscriptSaved && isWithinLimit}
									hasError={isTranscriptError}
								/>
							</div>
							<TextEditor
								id="meeting-transcript-editor"
								initialValue={transcriptContent}
								onChange={handleTranscriptChange}
								placeholder="Your transcript here"
								readOnly={!canEditTranscript}
								characterLimit={config.maxTranscriptLength}
								minHeight={config.layout.textEditorMinHeight}
								maxHeight={config.layout.textEditorMaxHeight}
							/>
							{!isWithinLimit && (
								<div className="flex items-start gap-1" role="alert" aria-live="assertive">
									<Icon icon={faInfoCircle} size={16} color="red-5" aria-hidden="true" />
									<Text size="caption" color="red-5">
										Transcripts have a maximum length of{" "}
										{config.maxTranscriptLength.toLocaleString()} characters
									</Text>
								</div>
							)}
							{transcriptData?.source && transcriptData.retrievedAt && (
								<TranscriptSourceNotation
									source={transcriptData.source}
									retrievedAt={transcriptData.retrievedAt}
								/>
							)}
						</>
					)}
				</div>
			)}
		</div>
	);
};

export default MeetingTranscriptSection;
